import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { getRemovalHistory, getRemovalStats, getRejectedRegistrations, getRejectedRegistrationStats } from '@/services/api';
import { 
  UserMinus, 
  Users, 
  Home,
  Shield,
  Clock,
  RefreshCw,
  Mail,
  Phone,
  FileText,
  AlertTriangle,
  Ban,
  XCircle
} from 'lucide-react';
import { toast } from 'sonner';

const AdminRemovalHistory = () => {
  const [removedUsers, setRemovedUsers] = useState([]);
  const [stats, setStats] = useState({ total: 0, tenants: 0, residents: 0, security: 0, admins: 0 });
  const [rejectedList, setRejectedList] = useState([]);
  const [rejectedStats, setRejectedStats] = useState({ total: 0, tenants: 0, residents: 0, security: 0 });
  const [activeTab, setActiveTab] = useState('removed');
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  const fetchData = async () => {
    try {
      setLoading(true);
      const roleFilter = filter === 'all' ? null : filter;
      const [historyData, statsData, rejData, rejStats] = await Promise.all([
        getRemovalHistory(roleFilter, 100),
        getRemovalStats(),
        getRejectedRegistrations(roleFilter, 100).catch(() => []),
        getRejectedRegistrationStats().catch(() => ({ total: 0, tenants: 0, residents: 0, security: 0 }))
      ]);
      setRemovedUsers(historyData);
      setStats(statsData);
      setRejectedList(rejData);
      setRejectedStats(rejStats);
    } catch (error) {
      toast.error('Failed to load history');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [filter]);

  const getRoleIcon = (user) => {
    if (user.is_tenant) return <Users className="h-5 w-5 text-blue-600" />;
    if (user.role?.includes('admin')) return <Shield className="h-5 w-5 text-amber-600" />;
    if (user.role?.includes('resident')) return <Home className="h-5 w-5 text-green-600" />;
    if (user.role?.includes('security')) return <Shield className="h-5 w-5 text-purple-600" />;
    return <Users className="h-5 w-5 text-slate-600" />;
  };

  const getRoleLabel = (user) => {
    if (user.is_tenant) return 'Tenant';
    if (user.role?.includes('admin')) return 'Admin';
    if (user.role?.includes('resident')) return 'Resident';
    if (user.role?.includes('security')) return 'Security';
    return 'User';
  };

  const getRoleBadgeClass = (user) => {
    if (user.is_tenant) return 'bg-blue-100 text-blue-700 border-blue-200';
    if (user.role?.includes('admin')) return 'bg-amber-100 text-amber-700 border-amber-200';
    if (user.role?.includes('resident')) return 'bg-green-100 text-green-700 border-green-200';
    if (user.role?.includes('security')) return 'bg-purple-100 text-purple-700 border-purple-200';
    return 'bg-slate-100 text-slate-700 border-slate-200';
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-IN', { 
      day: 'numeric',
      month: 'short', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-secondary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-removal-history">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Manrope' }}>
            Removal & Rejection History
          </h1>
          <p className="text-sm md:text-base text-slate-600">
            Track every removed user and every rejected registration across the society
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="w-[160px]" data-testid="filter-select">
              <SelectValue placeholder="Filter by role" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Users</SelectItem>
              <SelectItem value="admin">Admins</SelectItem>
              <SelectItem value="resident">Residents</SelectItem>
              <SelectItem value="tenant">Tenants</SelectItem>
              <SelectItem value="security">Security</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={fetchData} data-testid="refresh-btn">
            <RefreshCw className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-600">Total Removed</p>
                <p className="text-2xl font-bold text-slate-900">{stats.total}</p>
              </div>
              <UserMinus className="h-8 w-8 text-red-500" />
            </div>
          </CardContent>
        </Card>
        <Card className="border-amber-200 bg-amber-50">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-amber-700">Admins</p>
                <p className="text-2xl font-bold text-amber-900">{stats.admins || 0}</p>
              </div>
              <Shield className="h-8 w-8 text-amber-500" />
            </div>
          </CardContent>
        </Card>
        <Card className="border-blue-200 bg-blue-50">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-blue-700">Tenants</p>
                <p className="text-2xl font-bold text-blue-900">{stats.tenants}</p>
              </div>
              <Users className="h-8 w-8 text-blue-500" />
            </div>
          </CardContent>
        </Card>
        <Card className="border-green-200 bg-green-50">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-green-700">Residents</p>
                <p className="text-2xl font-bold text-green-900">{stats.residents}</p>
              </div>
              <Home className="h-8 w-8 text-green-500" />
            </div>
          </CardContent>
        </Card>
        <Card className="border-purple-200 bg-purple-50">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-purple-700">Security</p>
                <p className="text-2xl font-bold text-purple-900">{stats.security}</p>
              </div>
              <Shield className="h-8 w-8 text-purple-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Removal + Rejection Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-2 max-w-md" data-testid="removal-rejection-tabs">
          <TabsTrigger value="removed" data-testid="tab-removed-users">
            Removed Users {stats.total > 0 && <span className="ml-2 text-xs bg-red-100 text-red-700 rounded-full px-2 py-0.5">{stats.total}</span>}
          </TabsTrigger>
          <TabsTrigger value="rejected" data-testid="tab-rejected-registrations">
            Rejected Registrations {rejectedStats.total > 0 && <span className="ml-2 text-xs bg-orange-100 text-orange-700 rounded-full px-2 py-0.5">{rejectedStats.total}</span>}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="removed">
          {/* Removal History List */}
          <Card className="border-slate-200">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5 text-slate-600" />
                Removed Users
              </CardTitle>
              <CardDescription>Complete history of all user removals with reasons</CardDescription>
            </CardHeader>
            <CardContent>
          {removedUsers.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              <UserMinus className="h-12 w-12 mx-auto mb-3 text-slate-300" />
              <p className="font-medium">No removal records found</p>
              <p className="text-sm">Removed users will appear here</p>
            </div>
          ) : (
            <div className="space-y-4">
              {removedUsers.map((user) => (
                <div 
                  key={user.id} 
                  className="p-4 rounded-xl bg-red-50 border border-red-200"
                  data-testid={`removed-user-${user.id}`}
                >
                  <div className="flex flex-col md:flex-row md:items-start justify-between gap-3">
                    <div className="flex items-start gap-3">
                      <div className="h-10 w-10 rounded-full bg-red-100 flex items-center justify-center">
                        {getRoleIcon(user)}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 flex-wrap mb-1">
                          <span className="font-semibold text-slate-900">{user.name}</span>
                          <Badge className={getRoleBadgeClass(user)}>
                            {getRoleLabel(user)}
                          </Badge>
                          <Badge className="bg-red-100 text-red-700 border-red-200">
                            Removed
                          </Badge>
                        </div>
                        
                        {/* User Details */}
                        <div className="text-sm text-slate-600 space-y-1">
                          <p className="flex items-center gap-2">
                            <Mail className="h-3 w-3" />
                            {user.email}
                          </p>
                          {user.unit_number && (
                            <p className="flex items-center gap-2">
                              <Home className="h-3 w-3" />
                              Unit {user.unit_number}
                            </p>
                          )}
                          {user.phone && (
                            <p className="flex items-center gap-2">
                              <Phone className="h-3 w-3" />
                              {user.phone}
                            </p>
                          )}
                        </div>

                        {/* Removal Reason */}
                        {(user.removal_reason || user.vacancy_reason) && (
                          <div className="mt-3 p-3 bg-white rounded-lg border border-red-200">
                            <p className="text-xs font-medium text-red-600 mb-1 flex items-center gap-1">
                              <AlertTriangle className="h-3 w-3" />
                              Reason for Removal:
                            </p>
                            <p className="text-sm text-slate-700">{user.removal_reason || user.vacancy_reason}</p>
                          </div>
                        )}

                        {/* Removal Details */}
                        <div className="mt-2 text-xs text-slate-500 space-y-1">
                          {user.removal_requested_by_name && (
                            <p>Requested by: <span className="font-medium">{user.removal_requested_by_name}</span></p>
                          )}
                          {user.removal_approved_by_name && (
                            <p>Approved by: <span className="font-medium">{user.removal_approved_by_name}</span></p>
                          )}
                          {user.removed_by_name && !user.removal_approved_by_name && (
                            <p>Removed by: <span className="font-medium">{user.removed_by_name}</span></p>
                          )}
                          {user.vacancy_approved_by_name && (
                            <p>Vacancy approved by: <span className="font-medium">{user.vacancy_approved_by_name}</span></p>
                          )}
                        </div>
                      </div>
                    </div>
                    
                    <div className="text-xs text-slate-500 whitespace-nowrap flex items-center gap-1">
                      <Clock className="h-3 w-3" />
                      {formatDate(user.deactivated_at || user.removal_approved_at)}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
        </TabsContent>

        <TabsContent value="rejected">
          {/* Rejected Registrations */}
          <Card className="border-slate-200">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Ban className="h-5 w-5 text-orange-600" />
                Rejected Registrations
              </CardTitle>
              <CardDescription>
                Registration attempts that were rejected before activation.
                The user document is purged from the database; this view replays the audit log so the rejection is fully traceable.
              </CardDescription>
            </CardHeader>
            <CardContent>
              {rejectedList.length === 0 ? (
                <div className="text-center py-12 text-slate-500" data-testid="rejected-empty-state">
                  <Ban className="h-12 w-12 mx-auto mb-3 text-slate-300" />
                  <p className="font-medium">No rejected registrations yet</p>
                  <p className="text-sm">When a landlord or admin rejects a new registration, the audit entry will appear here.</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {rejectedList.map((entry) => (
                    <div key={entry.id} className="p-4 bg-orange-50 rounded-xl border border-orange-100" data-testid={`rejected-entry-${entry.id}`}>
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <XCircle className="h-4 w-4 text-orange-600" />
                            <h4 className="font-semibold text-slate-900">{entry.target_user_name || 'Unknown user'}</h4>
                            {entry.target_user_role && (
                              <Badge variant="outline" className="text-xs capitalize bg-white border-orange-200 text-orange-700">
                                {entry.target_user_role}
                              </Badge>
                            )}
                          </div>
                          <div className="text-sm text-slate-700 mb-2">
                            Rejected by <span className="font-medium">{entry.action_by_name}</span>
                            {entry.action_by_role && <span className="text-slate-500"> ({entry.action_by_role})</span>}
                          </div>
                          {entry.reason && (
                            <div className="mt-2 p-2 bg-white rounded-md border border-orange-200">
                              <p className="text-xs font-medium text-orange-700 mb-1 flex items-center gap-1">
                                <AlertTriangle className="h-3 w-3" />
                                Reason for Rejection
                              </p>
                              <p className="text-sm text-slate-700">{entry.reason}</p>
                            </div>
                          )}
                          {entry.details && (
                            <p className="text-xs text-slate-500 mt-2">{entry.details}</p>
                          )}
                        </div>
                        <div className="text-xs text-slate-500 whitespace-nowrap flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          {formatDate(entry.created_at)}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AdminRemovalHistory;
